import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

function Dashboard() {

  document.title = 'Dashboard';

  const { user } = useAuth();
  const [highPriorityJobs, setHighPriorityJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'

  useEffect(() => {
    const fetchHighPriorityJobs = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(`
            id,
            po_number,
            po_line_number,
            part_number,
            expected_delivery_date,
            status,
            high_priority,
            quantity_on_order,
            produced_quantity,
            shipped_quantity,
            shops ( id, name ),
            customers ( id, name, nickname, shipping_address, shipping_city, shipping_state, shipping_zip )
          `)
          .eq('high_priority', true)
          .order('expected_delivery_date', { ascending: true });
        if (error) throw error;
        setHighPriorityJobs(data || []);
      } catch (err) {
        console.error('Error fetching high priority jobs:', err.message);
        toast.error('Failed to fetch high priority jobs.');
      } finally {
        setLoading(false);
      }
    };

    fetchHighPriorityJobs();
  }, [user]);

  // Group jobs by shop (South Shop and North Shop)
  const groupedJobs = {
    southShop: highPriorityJobs.filter(
      (job) => job.shops && job.shops.name.toLowerCase().includes('south')
    ),
    northShop: highPriorityJobs.filter(
      (job) => job.shops && job.shops.name.toLowerCase().includes('north')
    ),
  };

  // Function to get status badge color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600 text-lg">Loading high priority jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">High Priority Jobs</h1>
          <div className="flex space-x-4 items-center">
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('card')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition duration-150 ${
                  viewMode === 'card'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Card View
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition duration-150 ${
                  viewMode === 'table'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Table View
              </button>
            </div>
            <Link
              to="/jobs"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-150 text-sm font-medium shadow-sm"
            >
              View All Jobs
            </Link>
          </div>
        </div>

        {highPriorityJobs.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              ></path>
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No High Priority Jobs</h3>
            <p className="mt-1 text-gray-500">There are currently no high priority jobs to display.</p>
          </div>
        ) : (
          <>
            {/* South Shop Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">South Shop</h2>
              {groupedJobs.southShop.length === 0 ? (
                <p className="text-gray-500">No high priority jobs for South Shop.</p>
              ) : viewMode === 'card' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedJobs.southShop.map((job) => (
                    <div
                      key={job.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                              PO #{job.po_number}
                            </h2>
                            <p className="text-sm text-gray-500">
                              Line: {job.po_line_number}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              job.status
                            )}`}
                          >
                            {job.status
                              .replace('_', ' ')
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                          </span>
                        </div>
                        <div className="space-y-2 mb-4">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Part:</span> {job.part_number}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Due:</span>{' '}
                            {job.expected_delivery_date
                              ? new Date(job.expected_delivery_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : 'KANBAN'}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Shop:</span>{' '}
                            {job.shops ? job.shops.name : 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Quantity Ordered:</span>{' '}
                            {job.quantity_on_order || 0}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Produced:</span>{' '}
                            {job.produced_quantity || 0}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Shipped:</span>{' '}
                            {job.shipped_quantity || 0}
                          </p>
                        </div>
                        {job.customers && (
                          <div className="border-t border-gray-100 pt-4">
                            <p className="text-sm font-medium text-gray-900 mb-2">Customer Info</p>
                            <div className="space-y-1 text-sm text-gray-600">
                              <p>
                                <span className="font-medium">Name:</span> {job.customers.name}
                              </p>
                              {job.customers.nickname && (
                                <p>
                                  <span className="font-medium">Nickname:</span>{' '}
                                  {job.customers.nickname}
                                </p>
                              )}
                              <p>
                                <span className="font-medium">Shipping:</span>{' '}
                                {job.customers.shipping_address}, {job.customers.shipping_city},{' '}
                                {job.customers.shipping_state} {job.customers.shipping_zip}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-100 p-4">
                        <Link
                          to={`/jobs/${job.po_number}`}
                          className="block w-full text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-150 text-sm font-medium shadow-sm"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto border-2 border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200  ">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          PO Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Part
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity Ordered
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produced
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Shipped
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupedJobs.southShop.map((job) => (
                        <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            PO #{job.po_number} (Line: {job.po_line_number})
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.part_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.expected_delivery_date
                              ? new Date(job.expected_delivery_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : 'KANBAN'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                job.status
                              )}`}
                            >
                              {job.status
                                .replace('_', ' ')
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.quantity_on_order || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.produced_quantity || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.shipped_quantity || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.customers ? job.customers.name : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Link
                              to={`/jobs/${job.po_number}`}
                              className="text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View Details
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* North Shop Section */}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">North Shop</h2>
              {groupedJobs.northShop.length === 0 ? (
                <p className="text-gray-500">No high priority jobs for North Shop.</p>
              ) : viewMode === 'card' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedJobs.northShop.map((job) => (
                    <div
                      key={job.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                              PO #{job.po_number}
                            </h2>
                            <p className="text-sm text-gray-500">
                              Line: {job.po_line_number}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              job.status
                            )}`}
                          >
                            {job.status
                              .replace('_', ' ')
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                          </span>
                        </div>
                        <div className="space-y-2 mb-4">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Part:</span> {job.part_number}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Due:</span>{' '}
                            {job.expected_delivery_date
                              ? new Date(job.expected_delivery_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : 'KANBAN'}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Shop:</span>{' '}
                            {job.shops ? job.shops.name : 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Quantity Ordered:</span>{' '}
                            {job.quantity_on_order || 0}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Produced:</span>{' '}
                            {job.produced_quantity || 0}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Shipped:</span>{' '}
                            {job.shipped_quantity || 0}
                          </p>
                        </div>
                        {job.customers && (
                          <div className="border-t border-gray-100 pt-4">
                            <p className="text-sm font-medium text-gray-900 mb-2">Customer Info</p>
                            <div className="space-y-1 text-sm text-gray-600">
                              <p>
                                <span className="font-medium">Name:</span> {job.customers.name}
                              </p>
                              {job.customers.nickname && (
                                <p>
                                  <span className="font-medium">Nickname:</span>{' '}
                                  {job.customers.nickname}
                                </p>
                              )}
                              <p>
                                <span className="font-medium">Shipping:</span>{' '}
                                {job.customers.shipping_address}, {job.customers.shipping_city},{' '}
                                {job.customers.shipping_state} {job.customers.shipping_zip}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-100 p-4">
                        <Link
                          to={`/jobs/${job.po_number}`}
                          className="block w-full text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-150 text-sm font-medium shadow-sm"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto border-2 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200 ">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          PO Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Part
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity Ordered
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produced
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Shipped
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupedJobs.northShop.map((job) => (
                        <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {job.po_number} (Line: {job.po_line_number})
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.part_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.expected_delivery_date
                              ? new Date(job.expected_delivery_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : 'KANBAN'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                job.status
                              )}`}
                            >
                              {job.status
                                .replace('_', ' ')
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.quantity_on_order || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.produced_quantity || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.shipped_quantity || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.customers ? job.customers.name : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Link
                              to={`/jobs/${job.po_number}`}
                              className="text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View Details
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;